import { useEffect, useState } from 'react'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle, Shield, Users } from 'lucide-react'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'
import { getAdminRoles, getPermissionsCatalog } from '@/lib/api/admin'
import {
  RolesPermissionsTab,
  type AdminRole,
  type PermissionDefinition,
} from '@/components/features/admin/RolesPermissionsTab'
import { TeamTab } from '@/components/features/admin/TeamTab'

const DEFAULT_PERMISSIONS: PermissionDefinition[] = []
const DEFAULT_ROLES: AdminRole[] = []

/**
 * SOLY-TEAM-001 — the Équipe page: one place for the people of the org (field
 * functions + Soly access) and the access roles. Replaces the old Utilisateurs
 * tab and the orphan /admin/employees skills page.
 */
export default function AdminPage() {
  const { user } = useAuth()

  const [roles, setRoles] = useState<AdminRole[]>(DEFAULT_ROLES)
  const [permissionsCatalog, setPermissionsCatalog] = useState<PermissionDefinition[]>(DEFAULT_PERMISSIONS)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [teamCount, setTeamCount] = useState<number | null>(null)

  const allowAdmin = user?.permissions?.includes(PERMISSIONS.USERS_MANAGE) === true

  const [activeTab, setActiveTab] = useState<'team' | 'roles'>('team')

  const orgName =
    user?.organizations?.find((o) => o.organizationId === user?.selectedOrganizationId)?.organizationName ||
    user?.organizations?.[0]?.organizationName ||
    'Votre organisation'

  const loadAdminData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const [rolesRes, permsRes] = await Promise.all([
        getAdminRoles(),
        getPermissionsCatalog(),
      ])

      const nextRoles: AdminRole[] = (rolesRes.roles || [])
        .map((r) => ({
          id: r.roleId,
          label: r.label,
          description: r.description || '',
          permissions: Array.isArray(r.permissions) ? r.permissions : [],
        }))
        .filter((r) => !!r.id)

      const nextPerms: PermissionDefinition[] = (permsRes.availablePermissions || [])
        .map((p) => ({
          id: p.id,
          description: p.description,
        }))
        .filter((p) => !!p.id)

      setRoles(nextRoles)
      setPermissionsCatalog(nextPerms)
    } catch (e) {
      console.error(e)
      setError("Impossible de charger l'administration")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!allowAdmin) {
      setIsLoading(false)
      return
    }
    loadAdminData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAdmin])

  if (!allowAdmin) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-10 px-4 space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Accès refusé</AlertTitle>
            <AlertDescription>
              Vous n'avez pas l'autorisation d'accéder à cette page.
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Équipe</h1>
              <p className="text-sm text-muted-foreground">
                L'équipe et les accès de {orgName}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {teamCount !== null ? (
                <Badge variant="outline" className="gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {teamCount} {teamCount > 1 ? 'employés' : 'employé'}
                </Badge>
              ) : null}
              <Badge variant="outline" className="gap-1">
                <Shield className="h-3.5 w-3.5" />
                {roles.length} rôles
              </Badge>
            </div>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erreur</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'team' | 'roles')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="team" data-testid="admin-team-tab">Équipe</TabsTrigger>
            <TabsTrigger value="roles" data-testid="admin-roles-tab">Rôles & permissions</TabsTrigger>
          </TabsList>

          <TabsContent value="team" className="mt-6">
            <TeamTab
              roles={roles}
              rolesLoading={isLoading}
              planningEnabled={user?.features?.planning}
              onCountChange={setTeamCount}
            />
          </TabsContent>

          <TabsContent value="roles" className="mt-6">
            <RolesPermissionsTab
              roles={roles}
              permissionsCatalog={permissionsCatalog}
              isLoading={isLoading}
              onReload={loadAdminData}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
